<template>
    <div class="location-autocomplete-wrapper" v-bind:class="{ active: (predictions && predictions.length > 0) || waiting }">
        <div>
            <input :placeholder="placeholderText ? placeholderText : ''" spellcheck="false" ref="location-autocomplete--input" autocomplete="off" id="location-autocomplete--input" type="text" v-model="inputValue" @keyup="onKeyup" @focus="onFocus" @blur="onBlur" class="form-input form-control"/>
        </div>
        <div class="location-autocomplete-result">
            <div v-for="item in predictions" v-show="!waiting" class="location-autocomplete-result_item" :key="item.id" v-if="itemVisibility(item)">
                <button @click="onPredictionClick(item)">{{ item.description }}</button>
            </div>
            <div v-show="waiting" class="location-autocomplete-result_item location-autocomplete-result_item_loader">
                <loader :size="24" :flag="true" :customAttribute="{tag: 'circle', children: 0, attribute:'stroke', value: '--main-color'}" ></loader>
            </div>
        </div>
    </div>
</template>

<script>
import bus from '@/services/bus-event';
import { mapGetters, mapActions } from 'vuex';
import { _parseGoogleResult, _normalizeText } from '../../services/utility';
let keyUpTimerId = 0;
let autocompleteService = null;
let geocoder = null;
let autocompleteSessionToken = null;

const toRegionCode = (pais) => {
    if (!pais) return null;
    const p = String(pais).toLowerCase();
    // Map internal codes to CLDR/ISO-3166-1 alpha-2 region codes.
    const map = {
        ar: 'AR',
        arg: 'AR',
        uy: 'UY',
        uru: 'UY',
        py: 'PY',
        par: 'PY',
        bo: 'BO',
        bol: 'BO',
        br: 'BR',
        bra: 'BR',
        cl: 'CL',
        chi: 'CL',
        chile: 'CL',
        co: 'CO',
        col: 'CO',
        mx: 'MX',
        mex: 'MX',
        pe: 'PE',
        per: 'PE',
        ec: 'EC',
        ecu: 'EC',
        es: 'ES',
        spa: 'ES',
        us: 'US',
        usa: 'US'
    };
    return map[p] || (p.length === 2 ? p.toUpperCase() : null);
};

export default {
    name: 'locationAutocomplete',
    props: ['initialValue', 'mustFocus', 'placeholderText'],
    data () {
        return {
            inputValue: this.initialValue || '',
            predictions: [],
            waiting: false,
            predictionSelected: {}
        };
    },
    computed: {
        ...mapGetters({
            currentPosition: 'geolocation/actual',
            currentZona: 'geolocation/actualZona'
        })
    },
    mounted () {
        this.language = this.$getLang();
        if (this.mustFocus) {
            this.$nextTick(() => {
                this.$refs['location-autocomplete--input'].focus();
            });
        }
        bus.on('drag_start', this.blur);
        bus.on('map_click', this.blur);
    },
    methods: {
        ...mapActions({
            keyboardSetVisibilityFlag: 'device/keyboardSetVisibilityFlag'
        }),
        /* eslint-disable no-undef */
        async _getPlacesLibrary () {
            if (typeof google === 'undefined' || !google.maps) {
                throw new Error('Google Maps not available');
            }

            if (typeof google.maps.importLibrary === 'function') {
                return google.maps.importLibrary('places');
            }

            // Legacy loader fallback (libraries=places).
            return {
                AutocompleteSuggestion: google.maps.places && google.maps.places.AutocompleteSuggestion ? google.maps.places.AutocompleteSuggestion : null,
                AutocompleteSessionToken: google.maps.places && google.maps.places.AutocompleteSessionToken ? google.maps.places.AutocompleteSessionToken : null
            };
        },
        _getOrCreateSessionToken () {
            // Session tokens reduce billing + are recommended with AutocompleteSuggestion.
            // We keep one token per "typing session" and reset it when user selects a prediction.
            if (autocompleteSessionToken) return autocompleteSessionToken;
            if (google.maps.places && google.maps.places.AutocompleteSessionToken) {
                autocompleteSessionToken = new google.maps.places.AutocompleteSessionToken();
            }
            return autocompleteSessionToken;
        },
        onKeyup () {
            this.waiting = true;
            if (keyUpTimerId) {
                clearTimeout(keyUpTimerId);
            }
            keyUpTimerId = setTimeout(() => {
                this.autocomplete();
            }, 800);
        },
        async autocomplete () {
            if (this.inputValue === '') {
                this.waiting = false;
                this.predictions = [];
                return;
            }

            // Prefer the new AutocompleteSuggestion API (recommended).
            try {
                const lib = await this._getPlacesLibrary();
                const AutocompleteSuggestion = lib && lib.AutocompleteSuggestion
                    ? lib.AutocompleteSuggestion
                    : (google.maps.places && google.maps.places.AutocompleteSuggestion ? google.maps.places.AutocompleteSuggestion : null);

                if (AutocompleteSuggestion && typeof AutocompleteSuggestion.fetchAutocompleteSuggestions === 'function') {
                    const center = this.currentZona
                        ? { lat: this.currentZona.lat, lng: this.currentZona.lng }
                        : (this.currentPosition ? { lat: this.currentPosition.lat, lng: this.currentPosition.lng } : null);

                    const request = {
                        input: this.inputValue,
                        sessionToken: this._getOrCreateSessionToken()
                    };

                    // Restrict by country/region when known (new API uses includedRegionCodes).
                    if (this.currentZona && this.currentZona.pais) {
                        const regionCode = toRegionCode(this.currentZona.pais);
                        if (regionCode) {
                            request.includedRegionCodes = [regionCode];
                            request.region = regionCode;
                        }
                    }

                    // radius/location are deprecated; use locationBias instead.
                    if (center) {
                        request.locationBias = { center, radius: 20000 };
                    }

                    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

                    this.waiting = false;
                    if (suggestions && suggestions.length) {
                        this.predictions = suggestions
                            .map((s) => s && s.placePrediction ? s.placePrediction : null)
                            .filter(Boolean)
                            .map((p) => {
                                // Adapt new PlacePrediction shape to the legacy fields used in the template/click handler.
                                return {
                                    place_id: p.placeId,
                                    description: p.text && p.text.text ? p.text.text : '',
                                    types: p.types || [],
                                    structured_formatting: {
                                        main_text: p.mainText && p.mainText.text ? p.mainText.text : (p.text && p.text.text ? p.text.text : '')
                                    }
                                };
                            })
                            .filter((p) => p.description);
                    } else {
                        this.predictions = [];
                    }
                    return;
                }
            } catch (e) {
                // If new API isn't available (older customers/loader), fall back to legacy service below.
                console.warn('AutocompleteSuggestion not available, falling back to AutocompleteService', e);
            }

            // Legacy fallback (kept for existing customers / older loader).
            if (!autocompleteService && window.google) {
                autocompleteService = new google.maps.places.AutocompleteService();
            }
            if (autocompleteService) {
                const centerLatLng = this.currentZona
                    ? new google.maps.LatLng(this.currentZona.lat, this.currentZona.lng)
                    : new google.maps.LatLng(this.currentPosition.lat, this.currentPosition.lng);

                const request = {
                    input: this.inputValue,
                    types: ['address'],
                    // bounds/location/radius are deprecated; use locationBias instead when possible.
                    locationBias: { center: centerLatLng, radius: 20000 }
                };

                if (this.currentZona && this.currentZona.pais) {
                    request.componentRestrictions = { country: this.currentZona.pais };
                }

                // getPlacePredictions is the closest equivalent (query predictions are not available in new API).
                autocompleteService.getPlacePredictions(request, this.autocompleteResults);
            } else {
                this.waiting = false;
                this.predictions = [];
            }
        },
        autocompleteResults (predictions, status) {
            console.log(predictions, status);
            this.waiting = false;
            // if (status !== google.maps.places.PlacesServiceStatus.OK) {
            //     return;
            // }
            if (predictions) {
                // Aca deberíamos filtrar todas las que no son cercanas.
                this.predictions = predictions;
            } else {
                let pred = {
                    description: this.inputValue,
                    custom: true
                };
                this.customAutocomplete(pred);
            }
        },
        customAutocomplete (item) {
            if (!geocoder && window.google) {
                geocoder = new google.maps.Geocoder();
            }

            let geocoderParam = {};
            geocoderParam.address = item.description;
            let callback = (results, status) => {
                if (status === 'OK' && results) {
                    results.forEach((value) => {
                        let pred = {
                            description: value.formatted_address,
                            custom: true
                        };
                        this.predictions.push(pred);
                    });
                }
            };
            this.predictions = [];
            geocoder.geocode(geocoderParam, callback);
        },
        itemVisibility (item) {
            if (item.custom) {
                return true;
            }
            let ignoreItem = false;
            let isStablishmentItem = false;
            if (item.types) {
                for (let i = 0; i < item.types.length; i++) {
                    if (item.types[i] === 'political') {
                        ignoreItem = true;
                    } else if (item.types[i] === 'establishment') {
                        isStablishmentItem = true;
                    }
                }
            } else {
                return false;
            }
            if (isStablishmentItem) {
                item._kind = 'establishment';
            } else {
                item._kind = 'route';
                let adRegex = /^\d+$/;
                if (this.currentZona.pais === 'col') {
                    adRegex = /^#\d+$/;
                }
                if (!/\d/.test(item.description)) { // No contiene número
                    ignoreItem = true;
                } else {
                    let tempArr = item.description.split(',');
                    if (tempArr.length >= 1) {
                        tempArr = tempArr[0].split(' ');
                        if (tempArr.length > 0) {
                            if (this.currentZona.pais === 'usa') { // si es USA, debe comenzar con número
                                if (!adRegex.test(tempArr[0])) {
                                    ignoreItem = true;
                                }
                            } else if (!adRegex.test(tempArr[tempArr.length - 1])) { // No termina con número
                                ignoreItem = true;
                            }
                        }
                    }
                }
            }
            return !ignoreItem;
        },
        onPredictionClick (item) {
            if (!geocoder && window.google) {
                geocoder = new google.maps.Geocoder();
            }

            let geocoderParam = {};
            if (item.custom) {
                geocoderParam.address = item.description;
            } else {
                geocoderParam.placeId = item.place_id;
            }

            if (geocoder && item) {
                // New API: selecting a prediction should end the session token.
                autocompleteSessionToken = null;

                if (item.structured_formatting && item.structured_formatting.main_text) {
                    item._text = item.structured_formatting.main_text;
                    console.log(item._text);
                } else {
                    item._text = item.description;
                }
                this.inputValue = item._text;
                let callback = (results, status) => {
                    return this.geocoderResults(results, status, item);
                };
                this.predictions = [];
                geocoder.geocode(geocoderParam, callback);
            }
        },
        geocoderResults (results, status, item) {
            if (status === 'OK' && results.length) {
                this.predictionSelected = _parseGoogleResult(results[0]);
                this.predictionSelected.location = results[0].geometry.location;
                let description = '';
                if (item._kind === 'establishment') { // It's route
                    description += item._text;
                }
                if (this.predictionSelected.calle && this.predictionSelected.numero !== -1) {
                    if (description !== '') {
                        description += ', ';
                    }
                    if (this.currentZona.pais === 'usa') { // condition for usa
                        description += this.predictionSelected.numero + ' ' + this.predictionSelected.calle;
                    } else {
                        description += this.predictionSelected.calle + ' ' + this.predictionSelected.numero;
                    }
                } else if (this.predictionSelected.description) {
                    description += this.predictionSelected.description;
                }
                if (description === '') {
                    description += item.description;
                }
                if (this.predictionSelected.ciudad && (_normalizeText(this.predictionSelected.ciudad) !== _normalizeText(this.currentZona.ciudad))) {
                    description += ', ' + this.predictionSelected.ciudad;
                } else if (!this.predictionSelected.ciudad && (_normalizeText(this.predictionSelected.provincia) !== _normalizeText(this.currentZona.ciudad))) {
                    description += ', ' + this.predictionSelected.provincia;
                }
                this.predictionSelected.description = description;
                this.inputValue = description;
                this.$emit('direction_change', this.predictionSelected);
            }
        },
        onFocus () {
            this.keyboardSetVisibilityFlag(true);
        },
        onBlur () {
            this.$emit('input_blur', this.inputValue);
            setTimeout(() => {
                this.keyboardSetVisibilityFlag(false);
            }, 250);
        },
        blur () {
            // Use $refs instead of getElementById for better Vue integration
            let elem = this.$refs['location-autocomplete--input'];
            if (elem) {
                elem.blur();
            }
        }
    },
    beforeDestroy () {
        bus.off('drag_start', this.blur);
        bus.off('map_click', this.blur);
    }
};
</script>

<style scoped>
    .location-autocomplete-wrapper {
        position: relative;
        z-index: 1000;
    }
    .location-autocomplete-wrapper.active {
        z-index: 1001;
    }
    .location-autocomplete-result {
        position: absolute;
        top: 2em;
        z-index: 1000;
        width: 100%;
    }
    .location-autocomplete-result_item {
        background: white;
        border: 1px solid rgba(0,0,0,.2);
        width: 100%;
        color: var(--text-softer-color);
    }
    .location-autocomplete-result_item:not(:last-child) {
        border-top: 0;
    }
    .location-autocomplete-result_item:first-child {
        border-top: 1px solid rgba(0,0,0,.2);
    }
    .location-autocomplete-result_item .loader,
    .location-autocomplete-result_item button {
        width: 100%;
        padding: .5em 1em;
        background: transparent;
        border: 0;
        text-align: left;
        border-radius: 0;
    }
    .location-autocomplete-result_item button:hover,
    .location-autocomplete-result_item button:focus {
        background: var(--text-color);
        color: var(--text-light-color);
    }
    input::placeholder {
        font-weight: 300;
    }
</style>



// WEBPACK FOOTER //
// src/components/elements/LocationAutocomplete.vue